import Player from "@vimeo/player";

class PdpHighlightsCarousel extends HTMLElement {
  constructor() {
    super();
    this.data = scrapeSectionData(this);
    this.carousel = this.querySelector("[data-video-carousel]");
    this.nav = this.querySelector("[data-video-carousel-nav]");
    this.swiper = null;
    this.players = new Map();

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        window.loadSwiper.then((swiperLib) => {
          this.swiperLib = swiperLib;
          this.initFunction();
        });
      });
    }

    window.loadSwiper.then((swiperLib) => {
      this.swiperLib = swiperLib;
      this.initFunction();
    });

    this.initMedia();

    const slideObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) =>
          entry.target.classList.toggle(
            "pdp-highlights-carousel__slide--visible",
            entry.isIntersecting,
          ),
        );
      },
      {
        root: this.querySelector(".pdp-highlights-carousel"),
        rootMargin: "0px 100% 0px 0px",
        // threshold: 0.25,
        threshold: [0.9],
      },
    );

    this.querySelectorAll(".swiper-slide").forEach((slide) => {
      slideObserver.observe(slide);
    });
  }

  initMedia() {
    // Play autoplay videos while they're on screen and pause any video that
    // scrolls or slides out of view, so only visible videos are decoding.
    // The observer clips to the carousel's overflow, so it covers swiping too.
    const mediaObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          if (!isIntersecting) {
            this.pauseMedia(target);
          } else if (
            target.classList.contains("media-is-autoplay--true") &&
            !target.dataset.userPaused
          ) {
            this.playMedia(target);
          }
        });
      },
      { threshold: 0.5 },
    );

    this.querySelectorAll(
      ".pdp-highlights-carousel__media.media__video",
    ).forEach((media) => {
      mediaObserver.observe(media);

      media
        .querySelector("[data-media-controls]")
        ?.addEventListener("click", (e) => {
          e.stopPropagation();

          if (media.dataset.playing === "true") {
            media.dataset.userPaused = "true";
            this.pauseMedia(media);
          } else {
            delete media.dataset.userPaused;
            this.playMedia(media);
          }
        });
    });
  }

  getPlayer(media) {
    const iframe = media.querySelector("iframe");
    if (!iframe) return media.querySelector("video");

    if (!this.players.has(iframe)) {
      this.players.set(iframe, new Player(iframe));
    }

    return this.players.get(iframe);
  }

  playMedia(media) {
    const player = this.getPlayer(media);
    if (!player) return;

    media.dataset.playing = "true";

    // play() rejects when playback is blocked (e.g. iOS Low Power Mode) or
    // interrupted by a pause. Vimeo players have no `paused` property.
    Promise.resolve(player.play()).catch(() => {
      if (player.paused !== false) media.dataset.playing = "false";
    });
  }

  pauseMedia(media) {
    const player = this.getPlayer(media);
    if (!player) return;

    media.dataset.playing = "false";
    Promise.resolve(player.pause()).catch(() => {});
  }

  initFunction() {
    if (!this.carousel) return;

    const { Swiper, Mousewheel } = this.swiperLib;

    const params = {
      modules: [Mousewheel],
      mousewheel: {
        enabled: true,
        forceToAxis: true,
        releaseOnEdges: true,
      },
      speed: 600,
      slidesPerView: 1,
      spaceBetween: 24,
      touchEventsTarget: "wrapper",
      direction: "horizontal",
      watchSlidesProgress: true,
      breakpoints: {
        768: {
          slidesPerView: 2,
        },
        // 1600: {
        //   slidesPerView: "auto",
        //   spaceBetween: 20,
        //   allowTouchMove: false,
        // },
      },
    };

    const userArrows = this.querySelectorAll(".swiper-arrow");
    userArrows.forEach((arrow) => {
      arrow.addEventListener("click", () => {
        if (arrow.classList.contains("swiper-arrow-next")) {
          this.swiper.isEnd ? this.swiper.slideTo(0) : this.swiper.slideNext();
        } else {
          this.swiper.isBeginning
            ? this.swiper.slideTo(this.swiper.slides.length - 1)
            : this.swiper.slidePrev();
        }
      });
    });

    this.swiper = new Swiper(this.carousel, params);

    this.swiper.on("lock", () => {
      this.nav.style.display = "none";
    });
    this.swiper.on("unlock", () => {
      this.nav.style.display = null;
    });

    this.nav.style.display = this.swiper.isLocked ? "none" : null;

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:load", (event) => {
        this.swiper.destroy();
      });
    }
  }
}

if (!customElements.get("pdp-highlights-carousel")) {
  customElements.define("pdp-highlights-carousel", PdpHighlightsCarousel);
}
